import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TemplateUsageInstructions } from 'components/models/FormTemplate';
import {
  addWogaaMetaData
} from 'components/utils/wogaa';
import AudienceMetadata from '../../../../models/AudienceMetadata';
import FormProps, { FormConfigProps, FormErrors, FormUpdateResponseData, UpdateScheduleProps, UpdateScheduleResponse, PublishStatusProps } from '../../../../models/FormProps';
import { addTask } from '../../../../utils/add_task';
import { getCsrfToken } from '../../../../utils/csrf';
import usePromptWindowUnload, { removePromptWindowUnload } from '../../../../utils/prompt_window_unload';
import { useDidUpdateEffect } from '../CustomHooks';
import Content from './Content';
import { SEARCH_PARAM_SKIP_TO_ADD_PERSONALISE_FIELDS } from '../edit_audience/constants';
import useServerLogger, { LogEventType } from '../../../../utils/use_server_logger';
import { sanitizeFormBodyForSave } from '../../../../utils/sanitize_form';
import { EditQuestionsIframeHandle } from './edit_questions_iframe/EditQuestionsIframe';
import { EditFormMessageToIframeType } from './edit_questions_iframe/types';

interface Props {
  form: FormProps & FormConfigProps;
  template: TemplateUsageInstructions;
  version: number;
  formPublishErrors: Array<string>;
  metadata: AudienceMetadata;
  metadataValuesWithHeaderUrl: string;
  hasAdditionalMetadata: boolean;
  canHaveAdditionalMetadata: boolean;
  updateUrl: string;
  updateScheduleUrl: string;
  updateFormImageUrl: string;
  previewUrl: string;
  formUrl: string;
  formsUrl: string;
  logUrl: string;
  createAnnouncementOnPgUrl: string;
  viewOnly: boolean;
  isShareableToPG: boolean;
  isParentForm: boolean;
  parentFormFaqUrl: string;
  wogaaPublishFormTrackingId: string;
  editQuestionIframeSource: string;
  formsURL: string;
  v2UserGuideUrl: string;
  userFormVersionPreference: 'v1' | 'v2';
  updateUserFormVersionPreferenceUrl: string;
  enforceV2Editor?: boolean;
}

export enum SaveStatus {
  Saved = 0,
  Saving,
  Error
}

export default function ContentContainer(props: Props): React.ReactElement {
  const { form, updateUrl, updateScheduleUrl, viewOnly, version, canHaveAdditionalMetadata, logUrl, userFormVersionPreference, updateUserFormVersionPreferenceUrl, enforceV2Editor } = props;
  const { title, instructions, body, ...formConfig } = form;

  const saveTimerRef = useRef<number>();
  const iframeRef = useRef<EditQuestionsIframeHandle>(null);

  const [useV2Editor, setUseV2Editor] = useState(() => enforceV2Editor || userFormVersionPreference === 'v2');

  const toggleEditorVersion = useCallback(async (value: 'v1' | 'v2') => {
    // v2 is enforced for everyone; ignore any request to drop back to v1
    // (covers the switch button and the iframe's SWITCH_TO_V1 message).
    if (enforceV2Editor && value === 'v1') return;
    await updatePreference(value);
    setUseV2Editor(value === 'v2');
    if (value === 'v1') {
      // switching back to v1, reload the page so that form state is updated
      window.location.reload();
    }
  }, [enforceV2Editor]);

  const formVersion = useRef<number>(version);

  const handleIframeVersionUpdate = useCallback((version: number) => {
    formVersion.current = version;
  }, []);

  const [formState, setFormState] = useState<FormProps>({
    title, instructions, body
  });
  const [formConfigState, setFormConfigState] = useState<FormConfigProps>(formConfig);

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [saveStatus, setSaveStatus] = useState(SaveStatus.Saved);

  const sendLog = useServerLogger(logUrl);

  const logSwitchReason = useCallback((reason: string) => {
    sendLog({
      event: LogEventType.EditorVersionSwitch,
      reason
    });
  }, [sendLog, form.id]);

  useEffect(() => {
    if (useV2Editor) {
      addWogaaMetaData('using_v2');
    } else {
      addWogaaMetaData('using_v1');
    }
  }, [useV2Editor]);

  useDidUpdateEffect(() => {
    if (!viewOnly && !useV2Editor) saveWithDelay();
  }, [formState]);
  usePromptWindowUnload(saveStatus !== SaveStatus.Saved);

  const onAddMetadata = canHaveAdditionalMetadata
    ? () => {
      sendLog({ event: LogEventType.Personalisation, trigger_from: 'add_more_fields' });
      window.location.href = `${updateUrl}/edit_audience?${SEARCH_PARAM_SKIP_TO_ADD_PERSONALISE_FIELDS}=1`;
    }
    : undefined;

  return (
    <>
      <Content
        {...props}
        form={{ id: form.id, title, instructions, body }}
        formConfig={formConfigState}
        onUpdateFormConfig={updateFormConfig}
        onUpdateSchedule={updateSchedule}
        formErrors={formErrors}
        saveStatus={saveStatus}
        onChangeForm={onChangeForm}
        onAddMetadata={onAddMetadata}
        useV2Editor={useV2Editor}
        toggleEditorVersion={toggleEditorVersion}
        iframeRef={iframeRef}
        onIframeVersionUpdate={handleIframeVersionUpdate}
        logSwitchReason={logSwitchReason}
        enforceV2Editor={enforceV2Editor}
      />
    </>
  );

  function onChangeForm(form: FormProps) {
    setFormState(form);
  }

  function saveWithDelay() {
    setSaveStatus(SaveStatus.Saving);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(save, 1000);

    return function cleanup() {
      clearTimeout(saveTimerRef.current);
    };
  }

  function save() {
    return addTask(updateFormTask);
  }

  async function updatePreference(preference: 'v1' | 'v2') {
    try {
      await fetch(updateUserFormVersionPreferenceUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({ form_version_preference: preference })
      });
    } catch (error) {
      // no easy way to receive errors on the frontend, so doing this for now
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }

  function updateFormTask() {
    setSaveStatus(SaveStatus.Saving);
    const sanitizedFormState = {
      ...formState,
      body: sanitizeFormBodyForSave(formState.body)
    };
    const options = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        // Signals to the server that this is a debounced autosave and should emit a debounced_save log event
        'X-Debounced-Save': '1'
      },
      body: JSON.stringify({
        form: {
          ...sanitizedFormState,
          lock_version: formVersion.current
        }
      })
    };

    return fetch(updateUrl, options)
      .then(response => {
        if (response.redirected) {
          removePromptWindowUnload();
          window.location.href = response.url;
          return;
        }

        return response.json();
      })
      .then(data => {
        if (data) {
          setFormErrors(processErrors(data.errors));
          setSaveStatus(data.success ? SaveStatus.Saved : SaveStatus.Error);
          if (data.success) formVersion.current = data.version;
        }
      })
      .catch(e => {
        console.log(e);
        setSaveStatus(SaveStatus.Error);
      });
  }

  /**
   * This function is similar to the other update call, with the key difference being
   * that this takes the request data as an argument (instead of being triggered
   * by a state change), and does not amend of the formState variable
   * within this component.
   *
   * This makes it possible to for the caller of this to handle the loading states and
   * manage the FormConfigProps by themselves.
   */
  async function updateFormConfig(data: FormConfigProps): Promise<FormUpdateResponseData | null> {
    const sanitizedFormState = {
      ...formState,
      body: sanitizeFormBodyForSave(formState.body)
    };
    const options = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken()
      },
      body: JSON.stringify({
        form: {
          ...sanitizedFormState,
          ...data,
          lock_version: formVersion.current
        }
      })
    };

    try {
      const response = await fetch(updateUrl, options);

      if (response.redirected) {
        window.location.href = response.url;
        return null;
      }

      const responseData: FormUpdateResponseData = await response.json();
      if (responseData.success) {
        formVersion.current = responseData.version;

        const { schedule, state, enable_email_copy_of_response } = responseData;
        setFormConfigState({
          schedule, state, enable_email_copy_of_response
        });
      }

      return responseData;
    } catch (e) {
      // no easy way to receive errors on the frontend, so doing this for now
      // eslint-disable-next-line no-console
      console.log(e);
      return null;
    }
  }

  async function updateSchedule(data: UpdateScheduleProps): Promise<UpdateScheduleResponse | null> {
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken()
      },
      body: JSON.stringify({
        form: {
          ...data,
          lock_version: formVersion.current
        }
      })
    };

    try {
      const response = await fetch(updateScheduleUrl, options);

      if (response.redirected) {
        window.location.href = response.url;
        return null;
      }

      const responseData: UpdateScheduleResponse = await response.json();
      if (responseData.success) {
        formVersion.current = responseData.version;

        const { schedule, state } = responseData;
        setFormConfigState(prev => ({
          ...prev, schedule, state
        }));

        const reason = state === PublishStatusProps.Published ? 'publish' : 'unpublish';
        iframeRef.current?.sendMessage({
          type: EditFormMessageToIframeType.VERSION_UPDATED,
          version: responseData.version,
          reason
        });
      }

      return responseData;
    } catch (e) {
      // no easy way to receive errors on the frontend, so doing this for now
      // eslint-disable-next-line no-console
      console.log(e);
      return null;
    }
  }

  function processErrors(errors: FormErrors): FormErrors {
    return {
      ...errors,
      bodyByElementID: errors.body?.reduce((hash, element) => { hash[element.element_id] = element.errors; return hash; }, {}) // group body errors by element id
    };
  }
}
